import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';

/** Topics from template PS_INSTRUCTION — copy scrubbed to Russian. */
const HELP = [
  {
    id: 'start',
    title: 'Первый вход владельца',
    text: 'Владелец входит как владелец системы. Переключение «Мои роли» меняет представление кабинета, но не подменяет реальные права на сервере. Предпросмотр роли не равен перевоплощению.',
  },
  {
    id: 'shell',
    title: 'Трёхколоночный рабочий экран',
    text: 'Общая оболочка: верхняя лента, кабинет ролей; слева — блокнот, календарь и события; справа — научная траектория, связи и поддержка.',
  },
  {
    id: 'notebook',
    title: 'Академический блокнот',
    text: 'Личная исследовательская память: источники, PDF/DOCX, заметки, карточки доказательств, коллекции и теги. По умолчанию приватно; обмен — только явным действием.',
  },
  {
    id: 'trajectory',
    title: 'Моя научная траектория',
    text: 'Лента, публикации, цели и вехи, ближайшие 90 дней, баланс нагрузки и объяснимое следующее академическое действие.',
  },
  {
    id: 'requests',
    title: 'Заявки в работе',
    text: 'Владелец видит все заявки, статусы, источники, ответственных, риски, уточнения и историю. Рабочие фильтры не создают копий сущности.',
  },
  {
    id: 'create',
    title: 'Создать заявку',
    text: 'Канонический маршрут: услуга → сведения → материалы → предварительные ответы → проверка и отправка. Одно подтверждение создаёт один идентификатор заявки.',
  },
  {
    id: 'roles',
    title: 'Пользователи и доступы',
    text: 'Единый модуль «Пользователи портала»: поиск, карточка, приглашения, очередь доступа, активация и журнал. Роль определяется серверным состоянием.',
  },
  {
    id: 'coverage',
    title: 'Покрытие услуг 11×72',
    text: 'Центр качества только для владельца: услуги × 10 слоёв покрытия. Статус «Готово» выводится из наличия источника или подтверждения.',
  },
  {
    id: 'preliminary',
    title: 'Предварительные ответы',
    text: 'Канонические записи по услугам. Изменение ответа хранит статус, источник, версию и журнал; содержательная переработка — отдельный цикл владельца.',
  },
  {
    id: 'ann',
    title: 'Объявления и опросы',
    text: 'Один модуль с переключателем типа. Объявление и опрос имеют общую историю и статистику; опрос поддерживает конструктор вопросов; консультант готовит только редактируемый черновик.',
  },
  {
    id: 'meeting',
    title: 'Встреча / дежурство',
    text: 'Одна запись события синхронизируется с календарём и получателем. Встреча — «Принять / Отклонить»; дежурство можно назначить группе.',
  },
  {
    id: 'docs',
    title: 'Исходные папки и документы',
    text: 'Центр документов владельца связывает файлы с объектами CRM. Файлы — в хранилище; статусы, права и журнал — в базе. Секреты и ключи не показываются.',
  },
  {
    id: 'superai',
    title: 'Консультант владельца',
    text: 'Привилегированные команды — только после интерпретации, плана и подтверждения владельца. Массовые и разрушающие действия требуют второго подтверждения.',
  },
  {
    id: 'connections',
    title: 'Академические связи и поддержка',
    text: 'Общие компоненты переиспользуются по ролям. Владелец видит свой контур. Вопрос-ответ и инструкция доступны отдельно и не требуют консультанта.',
  },
  {
    id: 'nav',
    title: 'Назад / вперёд / обновление',
    text: 'Вложенные маршруты закрываются без перезагрузки страницы. «Назад» восстанавливает состояние; создание, отправка и публикация идемпотентны.',
  },
  {
    id: 'security',
    title: 'Доступ и конфиденциальность',
    text: 'Разделы только для владельца отклоняются для других ролей. Журнал фиксирует создание и изменение. Служебные метки времени — с сервера. Закрытые объекты видны только участникам.',
  },
  {
    id: 'faq',
    title: 'Частые вопросы',
    text: 'Если действие не сохранилось: проверьте статус и автосохранение, вернитесь «Назад», откройте объект заново. При отказе в доступе не обходите адрес; проверьте роль и матрицу прав.',
  },
] as const;

export const OwnerInstructionPage = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [helpId, setHelpId] = useState<string>(HELP[0].id);

  const list = useMemo(
    () =>
      HELP.filter(
        (item) =>
          !query.trim() ||
          `${item.title} ${item.text}`.toLowerCase().includes(query.trim().toLowerCase()),
      ),
    [query],
  );

  useEffect(() => {
    document.body.classList.add('owner-uat-owner-route-open');
    return () => {
      document.body.classList.remove('owner-uat-owner-route-open');
    };
  }, []);

  useEffect(() => {
    if (!list.some((item) => item.id === helpId) && list[0]) {
      setHelpId(list[0].id);
    }
  }, [list, helpId]);

  const active = list.find((item) => item.id === helpId) ?? list[0] ?? HELP[0];

  const close = () => navigate(routes.owner);

  return (
    <div
      className="owner-uat-owner-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Инструкция"
      data-testid="owner-instruction-overlay"
    >
      <div className="r59-route-shell">
        <header className="r59-route-top">
          <span className="owner-uat-route-icon" aria-hidden="true">
            ♛
          </span>
          <div>
            <div className="owner-uat-route-kicker">Владелец системы · Zertteu Academy</div>
            <h1>Инструкция</h1>
            <p>Руководство владельца по разделам кабинета, сценариям, правам и восстановлению состояния</p>
          </div>
          <span className="spacer" />
          <button type="button" onClick={close}>
            ← Назад
          </button>
          <button type="button" onClick={close}>
            Закрыть ×
          </button>
        </header>

        <main className="r59-route-content">
          <section
            className="owner-uat-route-hero"
            style={{ '--owner-uat-accent': '#e2ad3a' } as CSSProperties}
          >
            <div>
              <div className="owner-uat-route-kicker">Руководство с учётом роли</div>
              <h2>Инструкция</h2>
              <p>
                Полное руководство по назначению каждой карточки, пошаговым сценариям, ограничениям,
                связанным разделам и восстановлению состояния.
              </p>
            </div>
            <div className="owner-uat-actions">
              <button type="button" className="owner-uat-btn" onClick={() => window.print()}>
                Печать / PDF
              </button>
              <button
                type="button"
                className="owner-uat-btn"
                onClick={() => {
                  notify('Открываю консультанта владельца');
                  navigate(routes.assistant);
                }}
              >
                Спросить консультанта
              </button>
            </div>
          </section>

          <section className="owner-uat-panel">
            <header>
              <div>
                <h3>Поиск по руководству</h3>
                <p>Название функции, кнопки, объекта или статуса.</p>
              </div>
              <span className="owner-uat-spacer" />
              <input
                className="owner-uat-input"
                style={{ minWidth: 260 }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Например: покрытие услуг, приглашение, Назад…"
                aria-label="Поиск по руководству"
              />
            </header>
            <div className="owner-uat-panel-body">
              <div className="owner-uat-help-layout">
                <nav className="owner-uat-help-nav" aria-label="Разделы инструкции">
                  {list.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={active.id === item.id ? 'active' : ''}
                      onClick={() => setHelpId(item.id)}
                    >
                      {item.title}
                    </button>
                  ))}
                  {!list.length ? (
                    <div className="owner-uat-empty">Ничего не найдено</div>
                  ) : null}
                </nav>
                <article className="owner-uat-help-section">
                  <div className="owner-uat-route-kicker">Инструкция владельца</div>
                  <h3>{active.title}</h3>
                  <p>{active.text}</p>
                  <h4>Пошаговый сценарий</h4>
                  <ol>
                    <li>Откройте соответствующую карточку кабинета владельца.</li>
                    <li>Проверьте входные данные и текущий статус.</li>
                    <li>
                      Выполните разрешённые действия; для публикации, отправки и разрушающих операций
                      подтвердите предпросмотр.
                    </li>
                    <li>После результата откройте историю / журнал или связанный объект.</li>
                  </ol>
                  <h4>Ограничения / права</h4>
                  <p>
                    Доступные действия зависят от вашей роли и прав на выбранный объект.
                  </p>
                  <h4>Если что-то пошло не так</h4>
                  <p>
                    Используйте «Назад», откройте объект из истории и проверьте статус. Обновление
                    страницы не требуется для восстановления интерфейса.
                  </p>
                  <div className="owner-uat-actions" style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="owner-uat-btn"
                      onClick={() => navigate(routes.portalSection('instructions'))}
                    >
                      Портальная инструкция
                    </button>
                  </div>
                </article>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
